/**
 * Shared logic for the GRID tile ("Grid H/A" in the Tarkib group).
 *
 * Grid differs from the other overlays: it is driven not by `overlay.mode` but
 * by the `lineup` replicant (a separate scene). So the tile sends
 * `lineup.show/hide` instead of `overlay.show` — the same chain as the "Efirga"
 * button in GridPanel; if the board is empty it is first filled from the formation.
 */
import type { ProjectState } from "@livcg/schema";
import { sendRaw } from "../../shared/sendRaw.js";

export type LineupSide = "home" | "away";

export const GRID_MODES: Record<string, LineupSide> = {
  grid_home: "home",
  grid_away: "away",
};

export function gridSideOf(mode: string): LineupSide | null {
  return GRID_MODES[mode] ?? null;
}

/** The id of the grid tile that is on air (or null). */
export function activeGridMode(state: ProjectState): string | null {
  const { visible, side } = state.lineup;
  return visible && side ? `grid_${side}` : null;
}

/** Is there a placement on the board, or at least are the slots filled so it can
 *  be filled from the formation — if empty, the tile stays disabled. */
export function gridHasData(state: ProjectState, side: LineupSide): boolean {
  if (state.grid[side].length > 0) return true;
  return state.lineup.slots.some((slot) => slot.playerId !== null);
}

/** If the board is empty, places from the formation, then puts it on air.
 *  Returns the error text (null = all good). */
export async function showGridOnAir(
  slug: string,
  side: LineupSide,
  state: ProjectState,
): Promise<string | null> {
  if (state.grid[side].length === 0) {
    const res = (await sendRaw("grid.autoFill", { slug, side })) as
      | { ok?: boolean; error?: string }
      | undefined;
    if (res && res.ok === false) {
      return res.error === "lineup_empty"
        ? "Avval Lineup'da o'yinchilarni tanlang"
        : (res.error ?? "Avto-joylash ishlamadi");
    }
  }
  await sendRaw("lineup.show", {
    slug,
    side,
    scheme: state.lineup.schemes[side],
    slots: state.lineup.slots,
  });
  return null;
}
